<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          State Match
        </template>
        <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button"
                  @keypress.enter.prevent="editPanel()"
                  @click="editPanel()"
                  :class="{ disabled: editOtherPanels }"
                  v-if="!editCurrentPanel"
                  :tabindex="editOtherPanels ? -1 : 0"
                >
                  <font-awesome-icon :icon="['far', 'edit']" size="lg" title="Edit" />
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-form style="padding: 1.15rem 0rem">
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Split Batch Option for Extracts
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons is-pulled-right"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in splitBatchOptions"
                        :key="index"
                        @click="form.SPLIT_BATCH_OPTION_FOR_EXTRACTS
                          = option.code"
                        :class="{'is-active'
                          : form.SPLIT_BATCH_OPTION_FOR_EXTRACTS === option.code
                        }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                    <div v-else class="is-pulled-right">
                      {{
                        displayValue(splitBatchOptions,
                        form.SPLIT_BATCH_OPTION_FOR_EXTRACTS)
                      }}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsForm,
    EsHeader,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.initialize(this.properties);
    this.splitBatchOptions = [
      {
        code: 'ALL',
        description: 'All',
      },
      {
        code: 'LEA',
        description: 'By LEA',
      },
    ];
  },
  mounted() {
    this.updateCache();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
  },
  methods: {
    ...mapActions({
      initProperties: 'propertiesModule/init',
    }),
    cancelPanel() {
      this.undoForm();
      this.hideNotification();
      this.editCurrentPanel = false;
      this.$emit('allowEditOtherPanels');
    },
    checkForFormChanges() {
      return this.compareObjects(this.cache, this.form);
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    displayValue(list, code) {
      const item = list.filter(l => l.code === code);
      if (item && item.length === 1) {
        return item[0].description;
      }
      return 'N/A';
    },
    editPanel() {
      this.editCurrentPanel = true;
      this.$emit('allowEditOtherPanels');
    },
    groupBySection(data, type) {
      return Object.entries(data)
        .filter(([key]) => key.includes(type))
        .map(([key, val]) => ({ [key.replace(type, '')]: val }));
    },
    initialize(data) {
      const allProperties = this.prepForDisplay(
        'form', this.groupBySection(data, 'DIRECTMATCH-'),
      );
      Object.filter = (obj, predicate) => Object.fromEntries(Object.entries(obj).filter(predicate));
      this.form = Object
        .filter(allProperties, ([name, value]) => name === 'SPLIT_BATCH_OPTION_FOR_EXTRACTS');
    },
    initialData() {
      return {
        editCurrentPanel: false,

        cache: null,
        form: null,
      };
    },
    prepForDisplay(type, arrFromDb) {
      const o = {};
      arrFromDb.map((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];
        o[key] = value;
        return 0;
      });
      return o;
    },
    prepForRequest() {
      const payload = [];
      let cached = null;
      let obj = null;
      let category = null;
      obj = this.form;
      cached = this.cache;
      category = 'DIRECTMATCH';
      Object.entries(obj).forEach(([property, value]) => {
        if (value !== cached[property]) {
          payload.push({ category, property, value });
        }
      });
      return payload;
    },
    undoForm() {
      this.form = JSON.parse(JSON.stringify(this.cache));
    },
    updateCache() {
      this.cache = JSON.parse(JSON.stringify(this.form));
    },
    async updatePanel() {
      this.$emit('allowEditOtherPanels');
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        this.hideNotification();
        return;
      }
      try {
        await axios({
          method: 'put',
          url: '/properties',
          data: this.prepForRequest(),
        });
        this.updateCache();
        await this.initProperties();
        this.showNotification('success', ['The State Match has been updated successfully.']);
        this.editCurrentPanel = false;
      } catch (e) {
        this.$emit('allowEditOtherPanels');
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table td
    vertical-align: middle
    &:first-child
      width: 30px
  a.disabled
    color: #999
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .property
    padding-left: 0
    font-size: 12px
    width: 50%
  .no-border
    border: 0
  .table-header-normal
    vertical-align: middle
</style>
